import {FormEvent, useEffect, useState} from "react";
import TemplateLogin from "../../../components/templates/TemplateLogin/TemplateLogin";
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import SigninInput from "../../../components/form/input-signin";
import Button from "../../../components/fundamentals/button";
import authService from "../../../services/auth.service";
import LogoIcon from "../../../components/fundamentals/icons/logo-icon";
import SignInWithGoogle from "./SignInWithGoogle";
import scriptService from "../../../services/script.service";
import thirdpartyService from "../../../services/thirdparty.service";

type FormValues = {
  email: string
  password: string
}

/* RAG-36 : délai avant de pouvoir renvoyer un code (le serveur limite aussi à 6/min). */
const RESEND_COOLDOWN_SECONDS = 30;

export default function Login(){
    const [isInvalidLogin, setIsInvalidLogin] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState<boolean>(false);
    const [googleSignInErrorMessage, setGoogleSignInErrorMessage] = useState<string>("");

    // RAG-36 : double authentification par code email.
    const [step, setStep] = useState<"password" | "code">("password");
    const [twoFactorToken, setTwoFactorToken] = useState<string>("");
    const [code, setCode] = useState<string>("");
    const [isInvalidCode, setIsInvalidCode] = useState<boolean>(false);
    const [codeInfo, setCodeInfo] = useState<string>("");
    const [resendCooldown, setResendCooldown] = useState<number>(0);
    const [trustDevice, setTrustDevice] = useState<boolean>(false);

    const { register, handleSubmit, reset } = useForm<FormValues>()
    const navigate = useNavigate()

     const onSubmit = async (values: FormValues) => {
        setIsLoading(true);
        setIsInvalidLogin(false);
        try{
            // Étape 1 : le mot de passe déclenche l'envoi du code, sans délivrer de jeton...
            // sauf si cet appareil est de confiance (accès direct, sans code).
            const result = await authService.authenticate(values);
            setIsLoading(false);
            if (result.loggedIn) {
                navigate('/');
                return;
            }
            setTwoFactorToken(result.twoFactorToken);
            setStep("code");
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch(e){
            reset();
            setIsInvalidLogin(true);
            setIsLoading(false);
        }

    }

    const onSubmitCode = async (event: FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setIsInvalidCode(false);
        setCodeInfo("");
        try {
            // Étape 2 : validation du code -> le jeton d'accès est enfin délivré.
            await authService.verifyCode(twoFactorToken, code, trustDevice);
            navigate('/');
        } catch (e) {
            setIsInvalidCode(true);
            setIsLoading(false);
        }
    }

    const handleResend = async () => {
        if (resendCooldown > 0) {
            return;
        }
        setIsInvalidCode(false);
        setCodeInfo("");
        try {
            const newToken = await authService.resendCode(twoFactorToken);
            setTwoFactorToken(newToken);
            setCode("");
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            setCodeInfo("Un nouveau code vous a été envoyé par email.");
        } catch (e) {
            setCodeInfo("Impossible de renvoyer le code pour le moment. Réessayez dans un instant.");
        }
    }

    const handleGoogleSignInResponse = async (response: any) => {
        setGoogleSignInErrorMessage("");

        setIsGoogleSignInLoading(true);

        try {
            await authService.loginWithGoogle(response.credential);

            navigate('/');
        } catch (e: any) {
            setIsGoogleSignInLoading(false);

            if (e.status) {
                setGoogleSignInErrorMessage(e.message)
            }
        }
    }

    // Décompte du délai de renvoi.
    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    useEffect(() => {
        (async () => {
            await scriptService.insertScript('head',
                {
                    src: "https://accounts.google.com/gsi/client",
                    async: true
                }
            )

            thirdpartyService.renderGoogleSignInButton(handleGoogleSignInResponse)
        })();
    }, []);

    return (
        <TemplateLogin>
            <div className="flex h-full w-full items-center justify-center">
                <div
                className={
                    "flex min-h-[500px] w-[640px] bg-grey-0 rounded-rounded justify-center transition-['min-height'] duration-300"
                }
                >
                    <div className="flex flex-col pt-12 w-full px-[120px] items-center">

                    {step === "password" ? (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-col items-center">

                            <LogoIcon />
                            <span className="inter-2xlarge-semibold mt-4 text-grey-90">
                                Bienvenue
                            </span>
                            <span className="inter-base-regular text-grey-50 mb-xlarge">
                                Connectez-vous à votre compte ci-dessous
                            </span>
                            <SigninInput
                                placeholder="Entrez votre email..."
                                {...register("email", { required: true })}
                                autoComplete="email"
                            />
                            <SigninInput
                                placeholder="Mot de passe..."
                                type={"password"}
                                {...register("password", { required: true })}
                                autoComplete="current-password"
                            />
                            {isInvalidLogin && (
                                <span className="text-rose-50 w-full mt-2 inter-small-regular">
                                    Les identifiants sont incorrect !
                                </span>
                            )}
                            <Button
                                className="rounded-rounded mt-4 w-[320px] inter-base-regular"
                                variant="primary"
                                size="large"
                                type="submit"
                                loading={isLoading}
                            >
                                Continuer
                            </Button>
                            {/*<SignInWithGoogle
                                isGoogleSignInLoading={isGoogleSignInLoading}
                                googleSignInErrorMessage={googleSignInErrorMessage}
                            />*/}
                            <span
                                className="inter-small-regular text-grey-50 mt-8 cursor-pointer"
                                onClick={() => navigate('/auth/password-reset')}
                            >
                                Mot de passe oublié
                            </span>
                        </div>
                    </form>
                    ) : (
                    <form onSubmit={onSubmitCode}>
                        <div className="flex flex-col items-center">

                            <LogoIcon />
                            <span className="inter-2xlarge-semibold mt-4 text-grey-90">
                                Vérification
                            </span>
                            <span className="inter-base-regular text-grey-50 mb-xlarge text-center">
                                Un code à 6 chiffres vient de vous être envoyé par email.
                                Saisissez-le ci-dessous pour finaliser la connexion.
                            </span>
                            <SigninInput
                                placeholder="Code reçu par email..."
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={code}
                                onChange={(e: any) => setCode(e.target.value)}
                            />
                            {isInvalidCode && (
                                <span className="text-rose-50 w-full mt-2 inter-small-regular">
                                    Code invalide ou expiré.
                                </span>
                            )}
                            {codeInfo && (
                                <span className="text-grey-50 w-full mt-2 inter-small-regular">
                                    {codeInfo}
                                </span>
                            )}
                            <label className="flex items-center gap-x-2 w-[320px] mt-4 cursor-pointer inter-small-regular text-grey-70">
                                <input
                                    type="checkbox"
                                    checked={trustDevice}
                                    onChange={(e) => setTrustDevice(e.target.checked)}
                                />
                                Faire confiance à cet appareil pendant 30 jours
                            </label>
                            <Button
                                className="rounded-rounded mt-4 w-[320px] inter-base-regular"
                                variant="primary"
                                size="large"
                                type="submit"
                                loading={isLoading}
                                disabled={code.length < 6}
                            >
                                Valider le code
                            </Button>
                            <span
                                className={
                                    "inter-small-regular mt-8 " +
                                    (resendCooldown > 0
                                        ? "text-grey-40 cursor-default"
                                        : "text-grey-50 cursor-pointer")
                                }
                                onClick={handleResend}
                            >
                                {resendCooldown > 0
                                    ? `Renvoyer le code (${resendCooldown}s)`
                                    : "Renvoyer le code"}
                            </span>
                        </div>
                    </form>
                    )}

                    </div>
                </div>
            </div>
        </TemplateLogin>

    )
}
